<template>
  <div
    class="p-4 pt-6 bg-surface-grey-light rounded-md w-full md:sticky md:top-24"
  >
    <h1 class="h4 pb-6">{{ $t('checkout.order_summary') }}</h1>

    <div class="mb-4 space-y-6 grow overflow-y-auto">
      <CartListItem v-for="item in cart.items" :key="item.id" :item="item" />
    </div>

    <div
      v-if="!hasTickets"
      class="text-center text-text text-sm flex gap-4 w-full items-center px-4"
    >
      <div class="h-px bg-surface-grey grow" />
      {{ $t('checkout.choose_tickets') }}
      <div class="h-px bg-surface-grey grow" />
    </div>

    <div
      class="p-4 mt-6 bg-surface-light rounded-md border border-surface-grey"
    >
      <div v-if="hasTickets" class="space-y-1 mb-6">
        <CartSummaryTable ref="summaryTable" />
      </div>

      <transition name="slidedown" mode="out-in">
        <div
          v-if="isFixed && hasTickets"
          class="fixed mb-0 bottom-0 border-t border-surface-grey z-10 left-0 right-0 w-full p-4 md:hidden bg-surface-light"
        >
          <BaseButton
            class="w-full"
            color="primary"
            :disabled="!hasTickets"
            :aria-disabled="!hasTickets ? 'true' : undefined"
            size="lg"
            :class="
              !hasTickets
                ? 'cursor-not-allowed opacity-50 pointer-events-none'
                : ''
            "
            type="submit"
          >
            <span v-if="$route.name == 'checkout-information'">
              {{ $t('checkout.continue_to_payment') }}
            </span>
            <span v-else>{{ $t('common.continue') }}</span>
            <ArrowRightIcon class="fill-current h-4 w-4 ml-4" />
          </BaseButton>
        </div>
      </transition>

      <div ref="proceedButtonRef">
        <BaseButton
          class="w-full"
          color="primary"
          v-if="$route.name != 'checkout-payment'"
          :disabled="!hasTickets"
          :aria-disabled="!hasTickets ? 'true' : undefined"
          size="lg"
          :class="
            !hasTickets
              ? 'cursor-not-allowed opacity-50 pointer-events-none'
              : ''
          "
          type="submit"
        >
          <span v-if="$route.name == 'checkout-information'">
            {{ $t('checkout.continue_to_payment') }}
          </span>
          <span v-else>{{ $t('common.continue') }}</span>
          <ArrowRightIcon class="fill-current h-4 w-4 ml-4" />
        </BaseButton>
      </div>

      <BaseButton
        color="default"
        class="w-full mt-4"
        v-if="
          ($route.name == 'checkout-tickets' ||
            $route.name == 'checkout-addons') &&
          !isEmbed()
        "
        size="lg"
        name="continue"
        value="continue"
        type="submit"
      >
        {{ $t('checkout.add_to_basket_and_continue') }}
        <AddToCartIcon class="fill-current h-6 w-6 ml-2" />
      </BaseButton>

      <BaseButton color="primary" size="lg" class="w-full mt-4" @click="resumePayment" v-if="$route.name == 'checkout-payment'">
        {{ $t('checkout.continue_to_payment_window') }}
      </BaseButton>
      <BaseButton color="muted" size="lg" class="w-full mt-4" @click="cancelOrder" v-if="$route.name == 'checkout-payment'">
        {{ $t('checkout.cancel_order') }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  import CartListItem from '@/components/checkout/CheckoutListItem.vue';
  import CartSummaryTable from '@/components/checkout/CheckoutSummaryTable.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { isEmbed } from '@/utils/isEmbed';

  import AddToCartIcon from '../icons/AddToCartIcon.vue';
  import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
  import BaseButton from '../ui/BaseButton.vue';

  const cart = useCartStore();
  const checkoutStore = useCheckoutStore();

  defineProps<{
    nextLink?: string;
    errors?: Partial<Record<string, string | undefined>>;
    values?: Partial<Record<string, string | undefined>>;
  }>();

  const summaryTable = ref<InstanceType<typeof CartSummaryTable>>();

  function calculateSummary() {
    if (summaryTable.value) {
      summaryTable.value.loadCodes();
    }
  }

  const isFixed = ref(false);
  const proceedButtonRef = ref<HTMLElement | null>(null);

  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    if (proceedButtonRef.value) {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          isFixed.value = !entry.isIntersecting;
        },
        { threshold: 0.01 }
      );
      observer.observe(proceedButtonRef.value);
    }
  });

  onUnmounted(() => {
    if (observer && proceedButtonRef.value) {
      observer.unobserve(proceedButtonRef.value);
      observer.disconnect();
    }
    cart.cleanUpEmptyEvents(true);
  });

  defineExpose({
    calculateSummary,
  });

  function resumePayment(){
    if(cart.unpaidOrder.psp == 'epay'){
      checkoutStore.restartEpayPayment(cart.unpaidOrder.psp, cart.unpaidOrder.order.payment_vars);
    }
    else if(cart.unpaidOrder.psp == 'quickpay10'){
      checkoutStore.startQuickPay10Payment();
    }
  }

  // watch will do the exit routing
  function cancelOrder(){
    checkoutStore.showCancelPaymentModal = true;
  }

  const hasTickets = computed(() =>
    cart.items.some(item => item.tickets.some(ticket => ticket.count > 0))
  );
</script>
